import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Users, Package, ShoppingCart, DollarSign, TrendingUp, UserCheck, Trash2, Edit, Grid3x3, Store, Loader2, Eye, Phone, Mail, MapPin, Building, FileText, ExternalLink, MessageSquare, Plus, Clock, PhoneCall, Calendar, AlertCircle, Send, Search, X, LogIn, Shield, Truck, MessageCircle, Database, FolderTree, Upload, Languages, BarChart3 } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { admin } from '@/lib/api';
import { useCurrency } from '@/lib/currency-context';
import { useAuth } from '@/lib/auth-context';
import { formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import SellerVerificationTab from '@/components/admin/SellerVerificationTab';
import RoleManagementTab from '@/components/admin/RoleManagementTab';
import PriceManagementTab from '@/components/admin/PriceManagementTab';
import ShippingManagementTab from '@/components/admin/ShippingManagementTab';
import DatabaseStatsTab from '@/components/admin/DatabaseStatsTab';
import BulkCategoryUpdate from '@/components/admin/BulkCategoryUpdate';
import { UsersTab, ProductsTab, OrdersTab, AdminCategoriesTab, UserDetailDialog } from '@/components/admin/dashboard';
import ChatManagementTab from '@/components/admin/ChatManagementTab';
import IntegrationsTab from '@/components/admin/IntegrationsTab';
import MarketEmailCampaign from '@/pages/admin/MarketEmailCampaign';
import EmailTemplateEditor from '@/pages/admin/EmailTemplateEditor';
import TranslationManagementTab from '@/components/admin/TranslationManagementTab';
import AdminSidebar from '@/components/admin/AdminSidebar';
import AdminOverviewTab from '@/components/admin/AdminOverviewTab';
import RestaurantEmailCampaign from '@/pages/admin/RestaurantEmailCampaign';
import SellerUploadsPage from '@/pages/admin/SellerUploadsPage';
import HomepageManagementPage from '@/pages/HomepageManagementPage';
import LogsTab from '@/components/admin/dashboard/LogsTab';
import ActivityLogTab from '@/components/admin/dashboard/ActivityLogTab';
import MailSettingsTab from '@/components/admin/MailSettingsTab';
import OutboundEmailsTab from '@/components/admin/OutboundEmailsTab';
import BulkDeleteSellerProducts from '@/components/admin/BulkDeleteSellerProducts';
import ReservationsTab from '@/components/admin/ReservationsTab';
import NewsletterSubscriptionsTab from '@/pages/admin/NewsletterSubscriptionsTab';
import MembershipManagementTab from '@/pages/admin/MembershipManagementTab';
import AiSearchTab from '@/components/admin/AiSearchTab';
import AIProductUpload from '@/components/products/AIProductUpload';

const AdminDashboardPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { formatPrice } = useCurrency();
  const { login: authLogin } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, type: '', id: '', name: '' });
  const [activeTab, setActiveTab] = useState('overview');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [userRoleFilter, setUserRoleFilter] = useState('all');
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [bulkDeleteDialog, setBulkDeleteDialog] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);
  const [selectedUserDetail, setSelectedUserDetail] = useState(null);
  const [showUserDetailDialog, setShowUserDetailDialog] = useState(false);
  
  // RBAC Permissions state
  const [myPermissions, setMyPermissions] = useState(null);
  
  // Permission check helper
  const hasPermission = (permission) => {
    if (!myPermissions) return false;
    if (myPermissions.is_super_admin) return true;
    return myPermissions.permissions?.includes(permission) || myPermissions.permissions?.includes('*');
  };

  const [pendingSellerApprovalCount, setPendingSellerApprovalCount] = useState(0);

  const refreshPendingSellerApprovalCount = useCallback(async () => {
    const p = myPermissions;
    if (!p) return;
    const can =
      p.is_super_admin ||
      (Array.isArray(p.permissions) &&
        (p.permissions.includes('sellers.view') || p.permissions.includes('*')));
    if (!can) {
      setPendingSellerApprovalCount(0);
      return;
    }
    try {
      const res = await admin.pendingSellersCountOnly();
      setPendingSellerApprovalCount(Number(res.data?.count) || 0);
    } catch {
      setPendingSellerApprovalCount(0);
    }
  }, [myPermissions]);

  useEffect(() => {
    if (!myPermissions) return undefined;
    refreshPendingSellerApprovalCount();
    const id = setInterval(refreshPendingSellerApprovalCount, 120000);
    return () => clearInterval(id);
  }, [myPermissions, refreshPendingSellerApprovalCount]);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const tab = searchParams.get('tab');
    const productId = searchParams.get('productId');
    if (tab) {
      setActiveTab(tab);
    } else if (productId) {
      setActiveTab('products');
    }
  }, [searchParams]);

  const clearProductQueryParams = () => {
    const next = new URLSearchParams(searchParams);
    next.delete('productId');
    next.delete('mode');
    setSearchParams(next, { replace: true });
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      // First fetch permissions
      const permsRes = await admin.getMyPermissions();
      setMyPermissions(permsRes.data);
      
      // Use Promise.allSettled to handle partial failures gracefully
      const results = await Promise.allSettled([
        admin.stats(),
        admin.users(),
        admin.products(),
        admin.orders(),
        admin.categories()
      ]);
      
      // Extract successful results, use empty defaults for failed ones
      const [statsRes, usersRes, productsRes, ordersRes, categoriesRes] = results;
      
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (usersRes.status === 'fulfilled') {
        // Handle both old format (array) and new format ({users: [], total: number})
        const usersData = usersRes.value.data;
        setUsers(Array.isArray(usersData) ? usersData : (usersData.users || []));
      }
      if (productsRes.status === 'fulfilled') setProducts(productsRes.value.data);
      if (ordersRes.status === 'fulfilled') setOrders(ordersRes.value.data);
      if (categoriesRes.status === 'fulfilled') setCategories(categoriesRes.value.data);
      
      // Only show error if critical endpoints failed
      const criticalFailed = [statsRes, usersRes].some(r => r.status === 'rejected');
      if (criticalFailed) {
        toast.error('Failed to load some admin data');
      }
    } catch (error) {
      console.error('Failed to fetch data:', error);
      toast.error('Failed to load admin data');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleChange = async (userId, newRole) => {
    try {
      await admin.updateRole(userId, newRole);
      toast.success(t('admin.roleUpdated') || 'User role updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update role');
    }
  };

  const handleDelete = async () => {
    const { type, id } = deleteDialog;
    try {
      if (type === 'user') {
        await admin.deleteUser(id);
        toast.success(t('admin.userDeleted') || 'User deleted');
      } else if (type === 'product') {
        await admin.deleteProduct(id);
        toast.success(t('admin.productDeleted') || 'Product deleted');
      } else if (type === 'category') {
        await admin.deleteCategory(id);
        toast.success(t('admin.categoryDeleted') || 'Category deleted');
      }
      setDeleteDialog({ open: false, type: '', id: '', name: '' });
      fetchData();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const handleOrderStatusChange = async (orderId, newStatus) => {
    if (!orderId) {
      // Called without params = just refresh data
      fetchData();
      return;
    }
    try {
      await admin.updateOrderStatus(orderId, newStatus);
      toast.success(t('admin.orderStatusUpdated') || 'Order status updated');
      fetchData();
    } catch (error) {
      toast.error('Failed to update order status');
    }
  };

  const statCards = [
    { label: t('admin.totalUsers'), value: stats?.total_users || 0, icon: Users, color: 'text-blue-600', tab: 'users', filter: 'all' },
    { label: t('admin.totalProducts'), value: stats?.total_products || 0, icon: Package, color: 'text-purple-600', tab: 'products', filter: null },
    { label: t('admin.totalOrders'), value: stats?.total_orders || 0, icon: ShoppingCart, color: 'text-green-600', tab: 'orders', filter: null },
    { label: t('admin.totalRevenue'), value: formatPrice(stats?.total_revenue || 0), icon: DollarSign, color: 'text-orange-600', tab: 'orders', filter: null }
  ];

  const handleStatCardClick = (tab, filter) => {
    setActiveTab(tab);
    if (tab === 'users' && filter) {
      setUserRoleFilter(filter);
    }
  };

  const handleUserBreakdownClick = (role) => {
    setActiveTab('users');
    setUserRoleFilter(role);
    setUserSearchQuery('');
  };

  const filteredUsers = users.filter(u => {
    // Role filter
    if (userRoleFilter !== 'all' && u.role !== userRoleFilter) {
      return false;
    }
    
    // Search filter
    if (userSearchQuery.trim()) {
      const query = userSearchQuery.toLowerCase().trim();
      const name = (u.name || '').toLowerCase();
      const email = (u.email || '').toLowerCase();
      const phone = (u.phone || '').toLowerCase();
      
      return name.includes(query) || email.includes(query) || phone.includes(query);
    }
    
    return true;
  }).sort((a, b) => {
    // Newest first by created_at
    const dateA = new Date(a.created_at || 0);
    const dateB = new Date(b.created_at || 0);
    return dateB - dateA;
  });

  // Bulk selection handlers
  const handleSelectUser = (userId) => {
    setSelectedUsers(prev => {
      if (prev.includes(userId)) {
        return prev.filter(id => id !== userId);
      } else {
        return [...prev, userId];
      }
    });
  };

  const handleSelectAllUsers = (checked) => {
    if (checked) {
      setSelectedUsers(filteredUsers.map(u => u.id));
    } else {
      setSelectedUsers([]);
    }
  };

  const isAllSelected = filteredUsers.length > 0 && selectedUsers.length === filteredUsers.length;
  const isSomeSelected = selectedUsers.length > 0 && selectedUsers.length < filteredUsers.length;

  const handleBulkDelete = async () => {
    setBulkDeleting(true);
    try {
      // Delete each selected user
      for (const userId of selectedUsers) {
        await admin.deleteUser(userId);
      }
      toast.success(t('admin.bulkDeleteSuccess') || `${selectedUsers.length} kullanıcı silindi`);
      setSelectedUsers([]);
      setBulkDeleteDialog(false);
      fetchData();
    } catch (error) {
      toast.error(t('admin.bulkDeleteError') || 'Toplu silme başarısız');
    } finally {
      setBulkDeleting(false);
    }
  };

  const handleViewUserDetail = (user) => {
    setSelectedUserDetail(user);
    setShowUserDetailDialog(true);
  };

  const handleImpersonateUser = async (user) => {
    // Determine redirect URL based on user role before API call
    const targetRole = user.role;
    let redirectUrl = '/dashboard';
    if (targetRole === 'seller') {
      redirectUrl = '/seller';
    } else if (targetRole === 'admin') {
      redirectUrl = '/admin';
    }
    
    try {
      const response = await admin.impersonateUser(user.id);
      
      // Store current admin session for later return
      const currentToken = localStorage.getItem('token');
      const currentUser = localStorage.getItem('user');
      localStorage.setItem('admin_original_token', currentToken);
      localStorage.setItem('admin_original_user', currentUser);
      
      // Login as the target user
      localStorage.setItem('token', response.data.access_token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      localStorage.setItem('impersonated_by', JSON.stringify(response.data.impersonated_by));
      
      toast.success(`${response.data.user.name} ${t('admin.impersonateSuccess') || 'hesabına giriş yapıldı'}`);
      
      // Close modal and redirect
      setShowUserDetailDialog(false);
      
      // Use window.location.replace for full page reload
      window.location.replace(redirectUrl);
    } catch (error) {
      console.error('Failed to impersonate user:', error);
      toast.error(error.response?.data?.detail || t('admin.impersonateError') || 'Hesaba giriş yapılamadı');
    }
  };

  const handleUserUpdated = (updatedUser) => {
    setSelectedUserDetail(updatedUser);
    fetchData(); // Refresh users list
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-slate-50">
        <div className="hidden lg:block w-64 bg-slate-900" />
        <div className="flex-1 p-6 lg:p-8">
          <Skeleton className="h-10 w-64 mb-8" />
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-8">
            {[...Array(6)].map((_, i) => (
              <Skeleton key={i} className="h-32 rounded-xl" />
            ))}
          </div>
          <Skeleton className="h-96 rounded-xl" />
        </div>
      </div>
    );
  }

  // Content renderer based on active tab
  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <AdminOverviewTab 
            stats={{
              totalRevenue: stats?.total_revenue || 0,
              totalOrders: stats?.total_orders || 0,
              totalProducts: stats?.total_products || 0,
              totalUsers: stats?.total_users || 0,
              totalSellers: users.filter(u => u.role === 'seller').length,
              pendingSellers: stats?.pending_sellers || 0
            }}
            formatPrice={formatPrice}
          />
        );
      case 'seller-verification':
        return (
          <SellerVerificationTab onSellersUpdated={refreshPendingSellerApprovalCount} />
        );
      case 'users':
        return (
          <UsersTab 
            users={users}
            filteredUsers={filteredUsers}
            userRoleFilter={userRoleFilter}
            setUserRoleFilter={setUserRoleFilter}
            userSearchQuery={userSearchQuery}
            setUserSearchQuery={setUserSearchQuery}
            selectedUsers={selectedUsers}
            setSelectedUsers={setSelectedUsers}
            handleSelectUser={handleSelectUser}
            handleSelectAllUsers={handleSelectAllUsers}
            isAllSelected={isAllSelected}
            isSomeSelected={isSomeSelected}
            handleRoleChange={handleRoleChange}
            handleViewUserDetail={handleViewUserDetail}
            setDeleteDialog={setDeleteDialog}
            setBulkDeleteDialog={setBulkDeleteDialog}
            hasPermission={hasPermission}
          />
        );
      case 'products':
        return (
          <ProductsTab 
            setDeleteDialog={setDeleteDialog}
            hasPermission={hasPermission}
            initialProductId={searchParams.get('productId')}
            initialModalMode={searchParams.get('mode') || 'edit'}
            onProductModalClose={clearProductQueryParams}
          />
        );
      case 'orders':
        return (
          <OrdersTab 
            orders={orders} 
            handleOrderStatusChange={handleOrderStatusChange} 
            formatPrice={formatPrice}
            hasPermission={hasPermission}
          />
        );
      case 'categories':
        return (
          <AdminCategoriesTab 
            categories={categories} 
            fetchData={fetchData} 
            setDeleteDialog={setDeleteDialog}
            hasPermission={hasPermission}
          />
        );
      case 'price-management':
        return hasPermission('products.edit') ? <PriceManagementTab /> : null;
      case 'shipping-management':
        return hasPermission('products.edit') ? <ShippingManagementTab /> : null;
      case 'role-management':
        return hasPermission('settings.roles') ? <RoleManagementTab /> : null;
      case 'chat-management':
        return <ChatManagementTab />;
      case 'market-emails':
        return hasPermission('settings.roles') ? <MarketEmailCampaign /> : null;
      case 'email-templates':
        return hasPermission('settings.roles') ? <EmailTemplateEditor /> : null;
      case 'data-sync':
        return hasPermission('settings.roles') ? <DataSyncTab /> : null;
      case 'integrations':
        return hasPermission('settings.roles') ? <IntegrationsTab /> : null;
      case 'db-stats':
        return hasPermission('settings.roles') ? <DatabaseStatsTab /> : null;
      case 'bulk-category':
        return hasPermission('products.edit') ? <BulkCategoryUpdate /> : null;
      case 'translations':
        return hasPermission('settings.roles') ? <TranslationManagementTab /> : null;
      case 'seller-uploads':
        return hasPermission('products.edit') ? <SellerUploadsPage /> : null;
      case 'bulk-delete-products':
        return hasPermission('products.edit') ? <BulkDeleteSellerProducts /> : null;
      case 'newsletter-subscriptions':
        return <NewsletterSubscriptionsTab />;
      case 'membership-management':
        return <MembershipManagementTab />;
      case 'finance-management':
        return hasPermission('settings.roles') ? <div className="text-center py-16 text-gray-500">Finans Yönetimi - Yakında</div> : null;
      case 'homepage-management':
        return hasPermission('settings.roles') ? <HomepageManagementPage /> : null;
      case 'restaurant-emails':
        return hasPermission('settings.roles') ? <RestaurantEmailCampaign /> : null;
      case 'reservations':
        return <ReservationsTab />;
      case 'system-logs':
        return hasPermission('settings.roles') ? <LogsTab /> : null;
      case 'mail-settings':
        return hasPermission('settings.roles') ? <MailSettingsTab /> : null;
      case 'outbound-emails':
        return hasPermission('settings.roles') ? <OutboundEmailsTab /> : null;
      case 'activity-logs':
        return hasPermission('settings.roles') ? <ActivityLogTab /> : null;
      case 'ai-search':
        return <AiSearchTab />;
      case 'ai-product':
        return <AIProductUpload embedded />;
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="admin-dashboard">
      {/* Sidebar */}
      <AdminSidebar 
        activeTab={activeTab}
        onTabChange={setActiveTab}
        collapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        hasPermission={hasPermission}
        pendingSellerApprovalCount={pendingSellerApprovalCount}
      />

      {/* Main Content */}
      <main className="flex-1 min-w-0 overflow-auto">
        <div className="p-4 lg:p-6 xl:p-8 pt-16 lg:pt-6">
          {renderContent()}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.confirmDelete') || 'Are you sure?'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.deleteWarning') || 'This action cannot be undone. This will permanently delete'} {deleteDialog.type}: <strong>{deleteDialog.name}</strong>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation Dialog */}
      <AlertDialog open={bulkDeleteDialog} onOpenChange={setBulkDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.bulkDeleteTitle') || 'Toplu Silme Onayı'}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.bulkDeleteWarning') || 'Bu işlem geri alınamaz.'} <strong>{selectedUsers.length}</strong> {t('admin.bulkDeleteCount') || 'kullanıcı kalıcı olarak silinecek.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkDeleting}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleBulkDelete} 
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              disabled={bulkDeleting}
            >
              {bulkDeleting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('admin.deleting') || 'Siliniyor...'}
                </>
              ) : (
                <>
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t('admin.deleteSelected') || 'Seçilenleri Sil'} ({selectedUsers.length})
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog> 

      {/* User Detail Dialog */}
      <UserDetailDialog
        open={showUserDetailDialog}
        onOpenChange={setShowUserDetailDialog}
        selectedUser={selectedUserDetail}
        onUserUpdated={handleUserUpdated}
        hasPermission={hasPermission}
        onImpersonate={handleImpersonateUser}
      />
    </div>
  );
};

export default AdminDashboardPage;
